// Live's Captions tab (#239, spec docs/specs/editor/captions.md): its header, its three states
// (no media; media and no captions; captions, with the tabs Presets, Transcript and Properties),
// "Generate captions" (part 2: the speech transcribed, `use-generate-captions.ts`, which the media
// panel holds) and "Import SRT, ASS, etc." by the button or a drop anywhere on the tab. Either
// replaces every caption with its words, cut by live's segmenter. Live's toasts are said in a
// line under the buttons here (web/AGENTS.md: no toasts), which the media panel keeps too.
import { Button, Text } from "@mantine/core";
import { Captions, ListChecks, Palette, SlidersHorizontal, UploadCloud } from "lucide-react";
import { useRef, useState, type DragEvent, type ReactNode } from "react";

import type { AspectRatio } from "../project";
import { newId, type Timeline, type TimelineEdit } from "../timeline";
import { PresetsTab } from "./PresetsTab";
import { ThemeEditor } from "./ThemeEditor";
import { cutCaptions } from "./recut";
import { MAX_SUBTITLE_BYTES, SUBTITLE_ACCEPT, formatOf, parseSubtitles, wordsOfCues } from "./subtitles";
import { Transcript, type Said } from "./Transcript";
import { noCaptionTrack } from "./transcribe";
import type { Playhead } from "../playhead";
import type { GenerateCaptions } from "./use-generate-captions";
import { MAX_CAPTIONS, MAX_TIMELINE_CAPTION_WORDS } from "./words";
import classes from "./captions.module.css";

export interface CaptionsTabProps {
  teamId: string;
  timeline: Timeline;
  aspectRatio: AspectRatio;
  playhead: Playhead;
  /** The member may not change this project: no import and no edit. */
  readOnly: boolean;
  onEdit: (edit: TimelineEdit) => void;
  onSeek: (ms: number) => void;
  /** The chosen caption, if one is chosen: the tab opens on Properties, as live's. */
  chosenCaption: string | null;
  /** Live's Generate and Regenerate, run by the media panel so a run outlives the tab. */
  generation: GenerateCaptions;
  /** The line under the buttons, kept by the media panel; null says nothing. */
  said: Said | null;
  onSay: (said: Said | null) => void;
}

/** Live's words for an import's end, by how many captions it made. */
function imported(count: number, name: string): string {
  return count === 1
    ? `Imported 1 caption segment from ${name}!`
    : `Imported ${count} caption segments from ${name}!`;
}

export function CaptionsTab(props: CaptionsTabProps) {
  const { timeline, readOnly, generation, said, onSay: setSaid } = props;
  const [importing, setImporting] = useState(false);
  const busy = importing || generation.busy;
  const chooser = useRef<HTMLInputElement>(null);
  const hasCaptions = timeline.clips.some((clip) => clip.kind === "caption");
  const hasMedia = timeline.clips.some((clip) => clip.kind === "video" || clip.kind === "audio");

  // Live's import: the file's words, cut into captions, replace every caption.
  const importFile = async (file: File) => {
    if (readOnly || busy) return;
    setSaid(null);
    // Read at once, so a file is capped first (2 MB is far past 5000 words).
    if (file.size > MAX_SUBTITLE_BYTES) {
      setSaid({ tone: "error", text: `"${file.name}" is too large to import (2 MB at most).` });
      return;
    }
    // A new caption track needs room among the tracks (the contract's 20).
    const noTrack = noCaptionTrack(timeline);
    if (noTrack !== null) {
      setSaid(noTrack);
      return;
    }
    setImporting(true);
    try {
      const format = formatOf(file.name);
      const cues = format === null ? [] : parseSubtitles(format, await file.text());
      if (cues.length === 0) {
        setSaid({ tone: "error", text: `Could not parse caption file "${file.name}". Please check the format.` });
        return;
      }
      const words = wordsOfCues(cues);
      if (words.length === 0) {
        setSaid({ tone: "error", text: `No subtitle words found in "${file.name}".` });
        return;
      }
      // Cut by the project's own paragraph settings and position (#264), live's.
      const segments = await cutCaptions(words, timeline.captions, props.aspectRatio);
      const total = segments.reduce((sum, segment) => sum + segment.words.length, 0);
      if (segments.length > MAX_CAPTIONS || total > MAX_TIMELINE_CAPTION_WORDS) {
        setSaid({
          tone: "error",
          text: `"${file.name}" holds more than a video's captions can (${MAX_CAPTIONS} captions, ${MAX_TIMELINE_CAPTION_WORDS} words).`,
        });
        return;
      }
      if (segments.length === 0) {
        setSaid({ tone: "info", text: "No caption segments were produced from the audio." });
        return;
      }
      props.onEdit({ type: "replaceCaptions", captions: segments, newTrackId: newId() });
      setSaid({ tone: "success", text: imported(segments.length, file.name) });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unknown error";
      setSaid({ tone: "error", text: `Failed to import caption file: ${message}` });
    } finally {
      setImporting(false);
    }
  };

  // Live takes a drop anywhere on the tab, its first file, whatever its kind.
  const drop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    if (file !== undefined) void importFile(file);
  };

  const importButton = (
    <Button
      variant="default"
      fullWidth
      leftSection={<UploadCloud size={16} aria-hidden />}
      loading={importing}
      disabled={readOnly || busy}
      onClick={() => chooser.current?.click()}
    >
      {importing ? "Importing file..." : "Import SRT, ASS, etc."}
    </Button>
  );
  // Live's Generate, named for the state it is in: "Generate" with no captions, "Regenerate" in
  // the Transcript's footer.
  const generateButton = (again: boolean): ReactNode => (
    <Button
      fullWidth
      loading={generation.busy}
      disabled={readOnly || busy}
      onClick={() => void generation.generate()}
    >
      {generation.busy
        ? again
          ? "Regenerating captions..."
          : "Generating captions..."
        : again
          ? "Regenerate captions"
          : "Generate captions"}
    </Button>
  );

  return (
    // A drop target, as live's tab: its buttons are the keyboard's way in.
    // oxlint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      className={classes.tab}
      onDragEnter={(event) => event.preventDefault()}
      onDragOver={(event) => event.preventDefault()}
      onDrop={drop}
    >
      <header className={classes.header}>
        <Text className={classes.title}>Captions</Text>
        <Text className={classes.subtitle}>Generate and edit dynamic captions</Text>
      </header>
      <input
        ref={chooser}
        type="file"
        accept={SUBTITLE_ACCEPT}
        hidden
        aria-label="Subtitle file"
        onChange={(event) => {
          const file = event.currentTarget.files?.[0];
          // Cleared after each pick, so the same file can be picked again.
          event.currentTarget.value = "";
          if (file !== undefined) void importFile(file);
        }}
      />
      {hasCaptions ? (
        <CaptionTabs {...props} importButton={importButton} regenerateButton={generateButton(true)} />
      ) : (
        <div className={classes.empty}>
          {hasMedia ? (
            <>
              <span className={classes.emptyIcon} aria-hidden>
                <Captions size={22} />
              </span>
              <Text className={classes.emptyTitle}>No captions yet</Text>
              <Text className={classes.emptyText}>
                Transcribe audio to automatically generate synchronized captions for your video.
              </Text>
              {generateButton(false)}
            </>
          ) : (
            <>
              <Text className={classes.emptyTitle}>No media on timeline</Text>
              <Text className={classes.emptyText}>
                Add audio or video clips to your timeline first to transcribe speech, or import a
                subtitle file directly.
              </Text>
            </>
          )}
          {importButton}
        </div>
      )}
      {said === null ? null : (
        <Text
          className={classes.said}
          data-tone={said.tone}
          role={said.tone === "error" ? "alert" : "status"}
        >
          {said.text}
        </Text>
      )}
    </div>
  );
}

type CaptionView = "presets" | "transcript" | "properties";

/** Live's tabs once there are captions: Presets, Transcript and Properties. */
function CaptionTabs(props: CaptionsTabProps & { importButton: ReactNode; regenerateButton: ReactNode }) {
  const [view, setView] = useState<CaptionView>(props.chosenCaption === null ? "transcript" : "properties");
  // A caption chosen on the canvas or the timeline opens Properties (live's).
  const [chosen, setChosen] = useState(props.chosenCaption);
  if (chosen !== props.chosenCaption) {
    setChosen(props.chosenCaption);
    if (props.chosenCaption !== null) setView("properties");
  }
  const tabs = [
    { id: "presets", label: "Presets", Icon: Palette },
    { id: "transcript", label: "Transcript", Icon: ListChecks },
    { id: "properties", label: "Properties", Icon: SlidersHorizontal },
  ] as const;
  return (
    <>
      <div className={classes.tabs} role="tablist" aria-label="Captions">
        {tabs.map((each) => {
          const content = (
            <>
              <each.Icon size={14} aria-hidden />
              {each.label}
            </>
          );
          return (
            <button
              key={each.id}
              type="button"
              role="tab"
              aria-selected={view === each.id}
              data-active={view === each.id}
              className={classes.tabButton}
              onClick={() => setView(each.id)}
            >
              {content}
            </button>
          );
        })}
      </div>
      {view === "properties" ? (
        <ThemeEditor
          teamId={props.teamId}
          timeline={props.timeline}
          aspectRatio={props.aspectRatio}
          readOnly={props.readOnly}
          chosenCaption={props.chosenCaption}
          onEdit={props.onEdit}
          onSay={props.onSay}
        />
      ) : view === "presets" ? (
        <PresetsTab
          teamId={props.teamId}
          timeline={props.timeline}
          aspectRatio={props.aspectRatio}
          readOnly={props.readOnly}
          onEdit={props.onEdit}
          onSay={props.onSay}
        />
      ) : (
        <Transcript
          timeline={props.timeline}
          playhead={props.playhead}
          readOnly={props.readOnly}
          onEdit={props.onEdit}
          onSeek={props.onSeek}
          onSay={props.onSay}
          importButton={props.importButton}
          regenerateButton={props.regenerateButton}
        />
      )}
    </>
  );
}
